import { useState } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { useNavigate } from 'react-router-dom'
import { 
  Layout, 
  LayoutDashboard, 
  Package, 
  MessageSquare, 
  ShoppingCart, 
  Settings as SettingsIcon, 
  LogOut,
  Plus,
  TrendingUp,
  Users,
  DollarSign
} from 'lucide-react'
import './Dashboard.css'

export default function Dashboard() {
  const { user, logout } = useAuth()
  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState('overview')

  const handleLogout = async () => {
    await logout()
    navigate('/')
  }

  if (!user) {
    return null
  }

  return (
    <div className="dashboard">
      <aside className="sidebar">
        <div className="sidebar-header">
          <h2>Sales AI</h2>
          <p className="merchant-name">Mon Commerce</p>
        </div>
        <nav className="sidebar-nav">
          <button 
            className={`nav-item ${activeTab === 'overview' ? 'active' : ''}`}
            onClick={() => setActiveTab('overview')}
          >
            <LayoutDashboard size={20} />
            Aperçu
          </button>
          <button 
            className={`nav-item ${activeTab === 'products' ? 'active' : ''}`}
            onClick={() => setActiveTab('products')}
          >
            <Package size={20} />
            Catalogue
          </button>
          <button 
            className={`nav-item ${activeTab === 'conversations' ? 'active' : ''}`}
            onClick={() => setActiveTab('conversations')}
          >
            <MessageSquare size={20} />
            Conversations
          </button>
          <button 
            className={`nav-item ${activeTab === 'orders' ? 'active' : ''}`}
            onClick={() => setActiveTab('orders')}
          >
            <ShoppingCart size={20} />
            Commandes
          </button>
          <button 
            className={`nav-item ${activeTab === 'settings' ? 'active' : ''}`}
            onClick={() => setActiveTab('settings')}
          >
            <SettingsIcon size={20} />
            Réglages
          </button>
        </nav>
        <div className="sidebar-footer">
          <button className="nav-item logout" onClick={handleLogout}>
            <LogOut size={20} />
            Déconnexion
          </button>
        </div>
      </aside>

      <main className="main-content">
        <header className="main-header">
          <h1>
            {activeTab === 'overview' && 'Aperçu'}
            {activeTab === 'products' && 'Catalogue'}
            {activeTab === 'conversations' && 'Conversations'}
            {activeTab === 'orders' && 'Commandes'}
            {activeTab === 'settings' && 'Réglages'}
          </h1>
          <div className="user-info">
            <span>{user.email}</span>
          </div>
        </header>

        <div className="content">
          {activeTab === 'overview' && <Overview />}
          {activeTab === 'products' && <ProductsTab />}
          {activeTab === 'conversations' && <ConversationsTab />}
          {activeTab === 'orders' && <OrdersTab />}
          {activeTab === 'settings' && <SettingsTab />}
        </div>
      </main>
    </div>
  )
}

function Overview() {
  return (
    <div className="overview">
      <div className="kpi-grid">
        <div className="kpi-card">
          <div className="kpi-icon">
            <DollarSign size={32} />
          </div>
          <div className="kpi-content">
            <div className="kpi-value">125,000 FCFA</div>
            <div className="kpi-label">Revenus ce mois</div>
            <div className="kpi-trend positive">+23%</div>
          </div>
        </div>
        <div className="kpi-card">
          <div className="kpi-icon">
            <ShoppingCart size={32} />
          </div>
          <div className="kpi-content">
            <div className="kpi-value">47</div>
            <div className="kpi-label">Commandes</div>
            <div className="kpi-trend positive">+12%</div>
          </div>
        </div>
        <div className="kpi-card">
          <div className="kpi-icon">
            <MessageSquare size={32} />
          </div>
          <div className="kpi-content">
            <div className="kpi-value">312</div>
            <div className="kpi-label">Conversations</div>
            <div className="kpi-trend positive">+45%</div>
          </div>
        </div>
        <div className="kpi-card">
          <div className="kpi-icon">
            <Users size={32} />
          </div>
          <div className="kpi-content">
            <div className="kpi-value">89%</div>
            <div className="kpi-label">Taux de conversion</div>
            <div className="kpi-trend positive">+5%</div>
          </div>
        </div>
      </div>

      <div className="card">
        <h3>Activité récente</h3>
        <div className="activity-list">
          <div className="activity-item">
            <div className="activity-info">
              <div className="activity-title">Nouvelle commande #1247</div>
              <div className="activity-time">Il y a 5 min</div>
            </div>
            <div className="activity-value">15,000 FCFA</div>
          </div>
          <div className="activity-item">
            <div className="activity-info">
              <div className="activity-title">Conversation résolue</div>
              <div className="activity-time">Il y a 15 min</div>
            </div>
            <div className="activity-status success">✓</div>
          </div>
          <div className="activity-item">
            <div className="activity-info">
              <div className="activity-title">Nouvelle conversation</div>
              <div className="activity-time">Il y a 32 min</div>
            </div>
            <div className="activity-status new">Nouveau</div>
          </div>
        </div>
      </div>
    </div>
  )
}

function ProductsTab() {
  const [products] = useState([
    { id: 1, name: 'Produit A', price: 5000, stock: 100 },
    { id: 2, name: 'Produit B', price: 15000, stock: 50 },
    { id: 3, name: 'Produit C', price: 25000, stock: 25 }
  ])

  return (
    <div className="products">
      <div className="card-header">
        <h3>Catalogue</h3>
        <button className="btn btn-primary">
          <Plus size={16} />
          Ajouter un produit
        </button>
      </div>
      <div className="table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th>Nom</th>
              <th>Prix (FCFA)</th>
              <th>Stock</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {products.map(product => (
              <tr key={product.id}>
                <td>{product.name}</td>
                <td>{product.price.toLocaleString()}</td>
                <td>{product.stock}</td>
                <td>
                  <button className="btn btn-sm btn-outline">Modifier</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

function ConversationsTab() {
  return (
    <div className="conversations">
      <div className="card">
        <h3>Conversations actives</h3>
        <p className="text-muted">Aucune conversation en cours</p>
      </div>
    </div>
  )
}

function OrdersTab() {
  return (
    <div className="orders">
      <div className="card">
        <h3>Commandes</h3>
        <p className="text-muted">Aucune commande récente</p>
      </div>
    </div>
  )
}

function SettingsTab() {
  return (
    <div className="settings">
      <div className="card">
        <h3>Configuration de l'agent IA</h3>
        <div className="form-group">
          <label>Nom de l'agent</label>
          <input type="text" defaultValue="Assistant" />
        </div>
        <div className="form-group">
          <label>Message d'accueil</label>
          <textarea defaultValue="Bonjour 👋 Que puis-je faire pour vous ?" rows={3} />
        </div>
        <div className="form-group">
          <label>Ton</label>
          <select defaultValue="chaleureux">
            <option value="chaleureux">Chaleureux</option>
            <option value="professionnel">Professionnel</option>
            <option value="formel">Formel</option>
          </select>
        </div>
        <button className="btn btn-primary">Sauvegarder</button>
      </div>
    </div>
  )
}
